import React, {useMemo, useRef, useState, useEffect} from 'react'
import {useReducedMotion} from 'framer-motion'
import {Box} from '@chakra-ui/react3'
import PropTypes from 'prop-types'

// Maps normalized progress (0..1) to character index
const idxFromProgress = (p, total) => {
    if (total <= 0) return 0
    return Math.max(0, Math.min(total, Math.round(p * total)))
}

const TypewrighterAnimation = ({
    children,
    from,
    to,
    topZone = 0.9, // 90% viewport height = area where progress = 0
    bottomZone = 0.1, // 10% viewport height = area where progress = 1
    ...props
}) => {
    const reduce = useReducedMotion()
    const text = typeof children === 'string' ? children : String(children ?? '')
    const chars = useMemo(() => Array.from(text), [text])

    const ref = useRef(null)
    const [coloredCount, setColoredCount] = useState(0)
    const startScrollY = useRef(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        // If prefers-reduced-motion is enabled (or emulated via MotionConfig),
        // skip animation and instantly show the final state.
        if (reduce) {
            setColoredCount(chars.length)
            return
        }

        // Function to update progress based on scroll position
        const update = () => {
            const rect = el.getBoundingClientRect()
            const vh = window.innerHeight

            // Define two anchor zones inside the viewport
            const topAnchor = vh * topZone
            const bottomAnchor = vh * bottomZone

            // Distance between anchors + element height
            const path = Math.max(1, topAnchor - bottomAnchor + rect.height)

            // Linear interpolation:
            // 0 when top >= topAnchor, 1 when bottom <= bottomAnchor
            const raw = (topAnchor - rect.top) / path
            const p = Math.min(1, Math.max(0, raw))

            // During the very first render we store the initial scroll position,
            // so the first visible block always starts with progress = 0.
            if (startScrollY.current === null) {
                startScrollY.current = window.scrollY
                setColoredCount(0)
                return
            }

            // Ignore if the user hasn’t actually scrolled yet
            if (Math.abs(window.scrollY - startScrollY.current) < 1) {
                setColoredCount(0)
                return
            }

            // Update colored character count
            setColoredCount(idxFromProgress(p, chars.length))
        }

        // Initial call + scroll listeners
        update()
        window.addEventListener('scroll', update, {passive: true})
        window.addEventListener('resize', update)

        // Cleanup on unmount
        return () => {
            window.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [chars.length, topZone, bottomZone, reduce])

    return (
        <Box ref={ref} aria-label={text} {...props}>
            {chars.map((char, index) =>
                char === ' ' ? (
                    ' '
                ) : (
                    <Box
                        as="span"
                        key={index}
                        color={index < coloredCount ? to : from}
                        transition={reduce ? 'none' : 'color .15s linear'} // Disable transitions when motion reduced
                        display="inline"
                        aria-hidden="true"
                    >
                        {char}
                    </Box>
                )
            )}
        </Box>
    )
}

TypewrighterAnimation.propTypes = {
    from: PropTypes.string,
    to: PropTypes.string,
    children: PropTypes.node,
    topZone: PropTypes.number,
    bottomZone: PropTypes.number
}

export default TypewrighterAnimation
